import type { ReactNode } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { Button, ErrorState, type ErrorStateDensity } from '@leanin/ui';
import { failureKind } from '../../lib/api-error';
import { withReturnPath } from '../../lib/return-path';
import { RouterLinkAs } from '../../lib/router-link';
import { RetryButton } from './RetryButton';

type Props = {
  title: ReactNode;
  error: unknown;
  /**
   * The surface is a list whose URL carries filters, search, tabs or a page
   * cursor. A rejected request then most likely came from a bad or stale
   * URL, so the state offers to reset the view to its unfiltered default.
   */
  filterable?: boolean;
  density?: ErrorStateDensity;
  /** False when the state sits inside a surface that already has a frame (a card, a dialog). */
  bordered?: boolean;
};

/**
 * Error state for a failed data load. The copy and the recovery action follow
 * the cause (`failureKind`), so we only say "try again" when trying again can
 * work: a server or network failure offers a retry, a bad link or filter
 * offers to clear it, and an access or sign-in failure says so plainly.
 */
export function LoadErrorState({
  title,
  error,
  filterable = false,
  density,
  bordered,
}: Props) {
  const t = useTranslations('Common');
  const navigate = useNavigate();
  const here = useRouterState({ select: (s) => s.location.href });

  let body: string;
  let primary: ReactNode = undefined;
  switch (failureKind(error)) {
    case 'transient':
      body = t('loadError.transientBody');
      primary = <RetryButton />;
      break;
    case 'invalid':
      if (filterable) {
        body = t('loadError.invalidFiltersBody');
        primary = (
          <Button variant="primary" onClick={() => void navigate({ to: '.', search: {} })}>
            {t('loadError.clearFilters')}
          </Button>
        );
      } else {
        body = t('loadError.invalidLinkBody');
      }
      break;
    case 'denied':
      body = t('loadError.deniedBody');
      break;
    case 'signedOut':
      body = t('loadError.signedOutBody');
      primary = (
        <Button
          variant="primary"
          href={withReturnPath('/sign-in', here)}
          linkAs={RouterLinkAs}
        >
          {t('loadError.signIn')}
        </Button>
      );
      break;
    case 'notFound':
      body = t('loadError.notFoundBody');
      break;
  }

  return (
    <ErrorState
      title={title}
      body={body}
      primary={primary}
      density={density}
      bordered={bordered}
    />
  );
}
